export const FLOOR_HEIGHT = 6.5;
export const DEFAULT_SHOPS_PER_FLOOR = 8;
export const WING_SPACING = 64;
export const SHOPS_PER_WING = 10;
export const MAX_FLOORS = 3;

export const ATRIUM_RADIUS = 14;
export const ATRIUM_HALF_LENGTH = 27.6;

export const wingForX = x =>
    Math.max(0, Math.round(x / WING_SPACING));

export function balconyEdge(z) {
    const oval =
        ATRIUM_RADIUS *
        Math.sqrt(
            Math.max(0, 1 - (z / ATRIUM_HALF_LENGTH) ** 2)
        );

    // Leave room for the rear escalator landings.
    return z > 20 ? Math.max(9, oval) : oval;
}

export function storefrontPose(z, side) {
    const step = 0.05;
    const slope =
        (balconyEdge(z + step) - balconyEdge(z - step)) /
        (2 * step);

    return {
        x: side * (balconyEdge(z) + 5.8),
        yaw: Math.atan2(-side, slope),
    };
}

export function storefrontEntrance(slot) {
    const yaw = slot.rotation?.[1] || 0;

    return {
        x: slot.position[0] + Math.sin(yaw) * 1.5,
        z: slot.position[2] + Math.cos(yaw) * 1.5,
    };
}

export function validCapacity(value) {
    const n = Number(value);
    return Number.isInteger(n) && n >= 4 && n <= 100;
}

export function eligibleStores(stores, isActive) {
    return stores
        .filter(
            store =>
                store.published === true &&
                isActive(store) &&
                typeof store.slug === 'string' &&
                store.slug.trim()
        )
        .sort((a, b) => a.id.localeCompare(b.id));
}

export function createLayout(
    count,
    capacity = DEFAULT_SHOPS_PER_FLOOR
) {
    const perFloor = validCapacity(capacity)
        ? Number(capacity)
        : DEFAULT_SHOPS_PER_FLOOR;

    const levels = Math.max(1, Math.ceil(count / perFloor));
    const across = Math.ceil(perFloor / SHOPS_PER_WING);
    const slots = [];

    for (let level = 0; level < levels; level++) {
        const floor = level % MAX_FLOORS;
        const band = Math.floor(level / MAX_FLOORS);

        for (let i = 0; i < perFloor; i++) {
            const part = Math.floor(i / SHOPS_PER_WING);
            const wing = band * across + part;
            const local = i % SHOPS_PER_WING;

            const spaces = Math.min(
                SHOPS_PER_WING,
                perFloor - part * SHOPS_PER_WING
            );

            const rows = Math.ceil(spaces / 2);
            const spacing = Math.min(18, 55 / rows);
            const row = Math.floor(local / 2);
            const west = local % 2 === 1;
            const z = (row - (rows - 1) / 2) * spacing;
            const pose = storefrontPose(z, west ? -1 : 1);
            const width = Math.min(12.6, spacing * 0.72);

            slots.push({
                slotId: `W${wing + 1}-${floor === 0 ? 'G' : `L${floor}`
                    }-${west ? 'W' : 'E'}${row + 1}`,
                wing,
                floor,
                zone: west ? 'west' : 'east',
                position: [
                    wing * WING_SPACING + pose.x,
                    floor * FLOOR_HEIGHT,
                    z,
                ],
                rotation: [0, pose.yaw, 0],
                size: [width, 5.3, 8],
                widthScale: width / 12.6,
                shopId: null,
                storefrontTemplate: 'general',
                active: true,
            });
        }
    }

    const wings = describeWings(slots);
    const floorCount = Math.min(MAX_FLOORS, levels);

    const floors = Array.from(
        { length: floorCount },
        (_, id) => ({
            id,
            y: id * FLOOR_HEIGHT,
            name: id === 0 ? 'Ground floor' : `Floor ${id}`,
        })
    );

    return {
        floors,
        slots,
        wings,
        wingCount: wings.length,
        floorCount,
        shopsPerFloor: perFloor,
    };
}

export function describeWings(slots) {
    const result = [];

    for (const slot of slots) {
        const id = slot.wing || 0;

        if (!result[id]) {
            result[id] = {
                id,
                x: id * WING_SPACING,
                floorCount: 1,
                shops: [],
            };
        }

        result[id].floorCount = Math.max(
            result[id].floorCount,
            slot.floor + 1
        );

        result[id].shops.push({
            floor: slot.floor,
            x: slot.position[0] - id * WING_SPACING,
            z: slot.position[2],
            yaw: slot.rotation[1],
            width: slot.size[0],
        });
    }

    return result.length
        ? result
        : [{ id: 0, x: 0, floorCount: 1 }];
}

export function placeStores(
    slots,
    stores,
    previous = [],
    saved = {}
) {
    const byId = new Map(stores.map(store => [store.id, store]));
    const used = new Set();

    const preferred = new Map(
        Object.entries(saved).map(([id, slot]) => [slot, id])
    );

    const old = new Map(
        previous.map(slot => [slot.slotId, slot.shopId])
    );

    const output = slots.map(slot => {
        const id =
            preferred.get(slot.slotId) || old.get(slot.slotId);

        if (!byId.has(id) || used.has(id)) {
            return { ...slot };
        }

        used.add(id);

        return {
            ...slot,
            shopId: id,
            shop: byId.get(id),
            storefrontTemplate: byId.get(id).template,
        };
    });

    const remaining = stores.filter(
        store => !used.has(store.id)
    );

    let next = 0;

    return output.map(slot => {
        if (slot.shopId || next >= remaining.length) {
            return slot;
        }

        const shop = remaining[next++];

        return {
            ...slot,
            shopId: shop.id,
            shop,
            storefrontTemplate: shop.template,
        };
    });
}